import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpBackend } from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable, of, throwError} from 'rxjs';
import {map, catchError, switchMap} from 'rxjs/operators';
import { Router } from '@angular/router';
import { PartnerService } from '../services/partner.service';

export interface ISession {
  csrf_access: string;
  csrf_refresh: string;
}


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private directHttp: HttpClient;

  constructor(private http: HttpClient, private router: Router, httpBackend: HttpBackend,
              private partnerSer: PartnerService) {
    this.directHttp = new HttpClient(httpBackend);
  }


  private token;
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  private loginUrl = 'auth/login';


  /** CSRF value that must accompany state-changing API calls made with the access cookie. */
  getCsrfAccess(): string {
    return localStorage.getItem('csrf_access');
  }

  /** CSRF value that must accompany the refresh call made with the refresh cookie. */
  getCsrfRefresh(): string {
    return localStorage.getItem('csrf_refresh');
  }

  /** Keep the CSRF values from a login, refresh or change-user response. */
  storeCsrf(session: ISession) {
    localStorage.setItem('csrf_access', session.csrf_access);
    localStorage.setItem('csrf_refresh', session.csrf_refresh);
  }

  /** True when this browser holds a signed-in user; an expired session surfaces as a 401. */
  isLoggedIn(): boolean {
    return !!localStorage.getItem('UserData');
  }

  logout() {
    const csrfAccess = this.getCsrfAccess();
    const csrfRefresh = this.getCsrfRefresh();
    if (csrfAccess) {
      // An expired access cookie cannot log out, so refresh once and retry to revoke the pair.
      this.postLogout(csrfAccess).pipe(
        catchError(() => csrfRefresh
          ? this.postRefresh(csrfRefresh).pipe(switchMap(session => this.postLogout(session.csrf_access)))
          : of(null))
      ).subscribe({ error: () => {} });
    }

    // RP-initiated logout: end the provider's session too, so a shared browser can't silently sign back in.
    const ssoLogoutUrl = localStorage.getItem('sso_logout_url');
    localStorage.clear();
    if (ssoLogoutUrl) {
      window.location.href = ssoLogoutUrl;
      return;
    }
    return this.router.navigate(['/login']);
  }

  /** Revoke the session behind the access cookie and clear both cookies. */
  private postLogout(csrfAccess: string) {
    return this.directHttp.post(`${environment.apiUrl}auth/logout`, {},
      { withCredentials: true, headers: new HttpHeaders({ 'X-CSRF-TOKEN': csrfAccess }) });
  }

  /** Rotate the cookie pair using the refresh cookie. */
  private postRefresh(csrfRefresh: string) {
    return this.directHttp.post<ISession>(`${environment.apiUrl}auth/refresh`, {},
      { withCredentials: true, headers: new HttpHeaders({ 'X-CSRF-TOKEN': csrfRefresh }) });
  }

  //
  // Login Method
  //
  getLogin(email: string, password: string) {
     return this.http.post(`${environment.apiUrl}auth/login`, { email, password },
     {
     }).pipe( map( (res: any) => {
       return res; }));
  }

  getSsoProviders() {
    return this.http.get(`${environment.apiUrl}auth/sso/providers`).pipe(map((res: any) => {
      return res;
    }));
  }

  completeSso(ticket: string) {
    return this.http.get(`${environment.apiUrl}auth/sso/session`, {
      params: { ticket }
    }).pipe(map((res: any) => {
      return res;
    }));
  }

  ssoStartUrl(slug: string) {
    return `${environment.apiUrl}auth/sso/${slug}/start`;
  }

  ssoErrorMessage(code: string): string {
    const messages = {
      token_expired: 'Your ADP sign-in expired. Please try again.',
      unknown_user: 'No myHRcounsel account is linked to this ADP user.',
      invalid_user: 'No myHRcounsel account is linked to this ADP user.',
      partner_miss_match: 'This ADP account is not linked to the expected partner.',
      invalid_header: 'ADP could not verify this sign-in. Please try again.',
      invalid_claims: 'ADP could not verify this sign-in. Please try again.',
      adp_invalid_client: 'ADP rejected the SSO client credentials. Confirm End-user/SSO Client ID and Secret in the ADP portal, or ask ADP to reset them.',
      adp_consent: 'ADP consent is missing for this app. Ask ADP to enable consent for your marketplace project.',
      invalid_token: 'ADP token exchange failed. Please try signing in again.',
      invalid_jwks_endpoint: 'Could not load ADP signing keys. Check network/certificate access to ADP.',
      unknown_iss: 'ADP is not configured for this environment.',
      provider_not_configured: 'Log in with ADP is not available yet.',
      unknown_provider: 'That sign-in method is not available.',
      access_denied: 'ADP sign-in was cancelled.',
      invalid_state: 'Sign-in could not be verified. Please try again.',
      user_inactive: 'This user is not active in myHRcounsel.',
      invalid_client: 'This account is not linked to an active client. Please call for administrative support.',
      invalid_or_expired_ticket: 'Your ADP sign-in expired. Please try again.',
      internal_error: 'ADP sign-in failed. Please try again.'
    };
    return messages[code] || 'ADP sign-in failed. Please try again.';
  }

  persistSession(data: any) {
    localStorage.clear();
    if (data.sso && data.sso.logout_url) {
      localStorage.setItem('sso_logout_url', data.sso.logout_url);
    }
    this.storeCsrf(data);
    localStorage.setItem('ClientID', data.UserData.ClientID);
    localStorage.setItem('UserData', JSON.stringify(data.UserData));
    localStorage.setItem('info', JSON.stringify(data));
    localStorage.setItem('notSubscribed', 'Your subscription does not include access to this feature. ' +
          'If you are interested in learning more, please email info@myhrcounsel.com or call (612) 339-1421');
    localStorage.setItem('showErisa', 'yes');
    if (data.UserData && data.UserData.client && data.UserData.client.PartnerID) {
      this.partnerSer.getPartner(data.UserData.client.PartnerID).subscribe((partnerData: any) => {
        if (partnerData.ServiceSolicitation !== null &&
            partnerData.ServiceSolicitation.trim().length > 0) {
              localStorage.setItem('notSubscribed', partnerData.ServiceSolicitation);
        }
        if (partnerData.ErisaCenter != null) {
            localStorage.setItem('showErisa', (partnerData.ErisaCenter === 1 ? 'yes' : 'no'));
        }
        if (partnerData.ToolsList != null) {
            localStorage.setItem('toolsList', partnerData.ToolsList );
        } else {
            localStorage.removeItem( 'toolsList' );
        }
      });
    }
  }


  changeUser(user_id: number) {
    return this.http.post(`${environment.apiUrl}auth/change-user`, {user_id: user_id},
    {
        headers: new HttpHeaders(
          {
            'accept': 'application/json'
          })
      }).pipe( map( (res: any) => {
       return res; }));
  }

  /** Turn the bearer token from a legacy sso-login payload into a cookie session. */
  sessionFromToken(accessToken: string) {
    return this.directHttp.post(`${environment.apiUrl}auth/session-from-token`, {},
      { withCredentials: true, headers: new HttpHeaders({ 'Authorization': `Bearer ${accessToken}` }) });
  }


  refreshToken(): Observable<ISession> {
    return this.postRefresh(this.getCsrfRefresh())
      .pipe(
          map( tok => {

           this.storeCsrf(tok);
           localStorage.setItem('info', JSON.stringify(tok));

           return tok;
         }),
         catchError(err => {
            this.logout();
            return throwError(() => err);
         })
      );

  }


  /**
   * Reset Password Method
   */
  sendResetPassword(data) {
    return this.http.post(`${environment.apiUrl}auth/reset-password`, data,
      {
        headers: new HttpHeaders(
          {
            'accept': 'application/json'
          })
      }).
    pipe(map((res: any) => { return res; }));
  }

  verifyToken(token) {
    return this.http.get(`${environment.apiUrl}auth/verify-token/` + token ,
      {
        headers: new HttpHeaders(
          {
            'accept': 'application/json'
          })
      }).
    pipe(map((res: any) => { return res; }));
  }

  resetPassword(data, token) {
    return this.http.post(`${environment.apiUrl}auth/verify-token/` + token , data,
      {
        headers: new HttpHeaders(
          {
            'accept': 'application/json'
          })
      }).
    pipe(map((res: any) => { return res; }));
  }

  /**
   * Forgot Password Method
   */
  forgotPassword(data) {
    return this.http.post(`${environment.apiUrl}auth/forget-password`, data,
      {
        headers: new HttpHeaders(
          {
            'accept': 'application/json'
          })
      }).
    pipe(map((res: any) => { return res; }));
  }

   /**
    * getSignup
    */
   logoutMethod() {
    localStorage.clear();
    this.router.navigate(['/login']);
   }

}
